import React, { Component } from 'react';
import AuthLayout from '../../layout/AuthLayout';
import perform_request from '../../provider/NetworkProvider';
import { createRoot } from "react-dom/client";
import '../../layout/AuthLayout.css';

const container = document.getElementById('react-app-container');

class SignIn extends Component {
    constructor(props) {
        super(props);

        // Read initial data from data_detail script tag
        const dataElement = document.getElementById('data_detail');
        const data = dataElement ? JSON.parse(dataElement.textContent) : {};
        const initialState = data.initialState || {};

        // Redirect URL after login/signup (e.g. job page when coming from public job link)
        const redirect = initialState.redirect || new URLSearchParams(window.location.search).get('redirect') || '';

        // Determine if this is creator domain
        this.isCreatorDomain = window.location.hostname.startsWith('creator.');

        this.state = {
            currentStep: 'email', // 'email' or 'otp'
            redirect,
            formData: {
                email: initialState.email || '',
                otp: '',
            },
            errors: {},
            isLoading: false,
            otpSent: false,
            otpResent: false,
            messages: []
        };
    }

    componentDidMount() {
        // Check for URL parameters for messages
        const urlParams = new URLSearchParams(window.location.search);
        const message = urlParams.get('message');
        
        if (message) {
            this.setState({messages: [{level: 'info', message: message}]});
        }
    }

    handleInputChange = (e) => {
        const { name, value } = e.target;
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [name]: value
            },
            errors: {
                ...prevState.errors,
                [name]: null
            }
        }));
    };

    sendOtp = async (e) => {
        e.preventDefault();

        const { email } = this.state.formData;

        // Validate email
        if (!email) {
            this.setState({
                errors: { email: 'Please enter your email address' }
            });
            return;
        }

        this.setState({ isLoading: true, errors: {} });

        try {
            const response = await perform_request('POST', 'signin/send-otp', false, {
                email
            });

            if (response.body && response.body.success) {
                this.setState({
                    currentStep: 'otp',
                    otpSent: true,
                    isLoading: false
                });
            } else {
                // Handle error actions
                const data = response.body || {};
                if (data.action === 'create_account') {
                    // No account found - redirect to sign up; preserve redirect so after signup we can return to job
                    const onboardingUrl = this.isCreatorDomain ? '/join' : '/welcome';
                    const redirectParam = this.state.redirect ? '?redirect=' + encodeURIComponent(this.state.redirect) : '';
                    window.location.href = onboardingUrl + redirectParam;
                } else if (data.action === 'onboarding_required') {
                    // Account needs onboarding - redirect; preserve redirect for after onboarding
                    const onboardingUrl = this.isCreatorDomain ? '/join' : '/welcome';
                    const redirectParam = this.state.redirect ? '?redirect=' + encodeURIComponent(this.state.redirect) : '';
                    window.location.href = onboardingUrl + redirectParam;
                } else {
                    this.setState({
                        errors: { email: data.message || 'An error occurred' },
                        isLoading: false
                    });
                }
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            this.setState({
                errors: { email: error.error || 'An error occurred. Please try again.' },
                isLoading: false
            });
        }
    };

    verifyOtp = async (e) => {
        e.preventDefault();

        const { email, otp } = this.state.formData;

        // Validate OTP
        if (!otp || otp.length !== 6) {
            this.setState({
                errors: { otp: 'Please enter a 6-digit code' }
            });
            return;
        }

        this.setState({ isLoading: true, errors: {} });

        try {
            const body = { email, otp };
            if (this.state.redirect) body.redirect_url = this.state.redirect;
            const response = await perform_request('POST', 'signin/verify-otp', false, body);

            if (response.body && response.body.success) {
                // Show success message briefly then redirect (API returns redirect_url, may be job page)
                this.setState({ isLoading: false });
                window.location.href = response.body.redirect_url;
            } else {
                const data = response.body || {};
                this.setState({
                    errors: { otp: data.message || 'An error occurred' },
                    isLoading: false
                });
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            this.setState({
                errors: { otp: error.error || 'An error occurred. Please try again.' },
                isLoading: false
            });
        }
    };

    resendOtp = async () => {
        const { email } = this.state.formData;
        
        this.setState({ isLoading: true });

        try {
            const response = await perform_request('POST', 'signin/send-otp', false, {
                email
            });

            if (response.body && response.body.success) {
                this.setState({ 
                    isLoading: false,
                    otpResent: true 
                });
                // Clear the resent flag after 3 seconds
                setTimeout(() => {
                    this.setState({ otpResent: false });
                }, 3000);
            } else {
                const data = response.body || {};
                this.setState({ 
                    isLoading: false,
                    errors: { otp: data.message || 'Failed to resend code' }
                });
            }
        } catch (error) {
            console.error('Error resending OTP:', error);
            this.setState({ 
                isLoading: false,
                errors: { otp: error.error || 'Failed to resend code. Please try again.' }
            });
        }
    };

    backToEmail = () => {
        this.setState({
            currentStep: 'email',
            formData: {
                ...this.state.formData,
                otp: ''
            },
            errors: {}
        });
    };

    handleOtpInput = (e) => {
        const otp = e.target.value.replace(/\D/g, '').slice(0, 6);
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                otp
            },
            errors: {
                ...prevState.errors,
                otp: null
            }
        }), () => {
            // Auto-submit when 6 digits entered
            if (otp.length === 6) {
                this.verifyOtp(new Event('submit'));
            }
        });
    };

    renderEmailStep = () => {
        const { email } = this.state.formData;
        const { errors, isLoading, messages } = this.state;

        const illustrationText = (
            <div className="text-dark">
                <h1 className="mb-2 fw-bold">Grow Your Social Presence</h1>
                <p className="mb-0 opacity-75">Connect with creators, manage campaigns, and scale your brand's influence</p>
            </div>
        );

        return (
            <AuthLayout showMessages={false} showCopyright={true} illustrationText={illustrationText}>
                <h2 className="mb-2">
                    Welcome to Iprofitsocial
                </h2>
                
                <p className="text-secondary mb-3">
                    Enter your email id
                </p>

                {messages && messages.length > 0 && (
                    <>
                        {messages.map((message, index) => (
                            <div key={index}
                                 className={`alert alert-${message.level} alert-dismissible mb-3`}
                                 role="alert">
                                <div className="d-flex">
                                    <div>
                                        {message.level === 'success' ? (
                                            <i className="icon alert-icon ti ti-check"></i>
                                        ) : (
                                            <i className="icon alert-icon ti ti-alert-triangle"></i>
                                        )}
                                    </div>
                                    <div>{message.message}</div>
                                </div>
                                <button className="btn-close" data-bs-dismiss="alert" aria-label="close"></button>
                            </div>
                        ))}
                    </>
                )}

                <form onSubmit={this.sendOtp} autoComplete="off" noValidate>
                    <div className="mb-3">
                        <label className="form-label">
                            Email
                        </label>
                        <input
                            type="email"
                            name="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            placeholder="Enter your email id"
                            value={email}
                            onChange={this.handleInputChange}
                            autoComplete="email"
                            autoFocus
                            disabled={isLoading}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>

                    <div className="mb-3">
                        <button 
                            type="submit" 
                            className="btn btn-primary w-100" 
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Sending...
                                </>
                            ) : 'Continue'}
                        </button>
                    </div>
                    
                    <div className="text-center">
                        <span className="text-secondary">
                            Don't have an account?{' '}
                            <a 
                                href={this.isCreatorDomain ? '/join' : '/welcome'}
                            >
                                Sign up
                            </a>
                        </span>
                    </div>
                </form>
            </AuthLayout>
        );
    };

    renderOtpStep = () => {
        const { email, otp } = this.state.formData;
        const { errors, isLoading, otpResent, messages } = this.state;

        const illustrationText = (
            <div className="text-dark">
                <h1 className="mb-2 fw-bold">Grow Your Social Presence</h1>
                <p className="mb-0 opacity-75">Connect with creators, manage campaigns, and scale your brand's influence</p>
            </div>
        );

        return (
            <AuthLayout showMessages={false} showCopyright={true} illustrationText={illustrationText}>
                <button
                    type="button"
                    className="auth-layout-back-button mb-3"
                    onClick={this.backToEmail}
                    disabled={isLoading}
                >
                    <i className="ti ti-arrow-left"></i>
                </button>

                <h2 className="mb-2">
                    Check your email
                </h2>
                <p className="text-secondary mb-3">
                    We sent a verification code to<br />
                    <strong>{email}</strong>
                </p>

                {messages && messages.length > 0 && (
                    <>
                        {messages.map((message, index) => (
                            <div key={index}
                                 className={`alert alert-${message.level} alert-dismissible mb-3`}
                                 role="alert">
                                <div className="d-flex">
                                    <div>
                                        {message.level === 'success' ? (
                                            <i className="icon alert-icon ti ti-check"></i>
                                        ) : (
                                            <i className="icon alert-icon ti ti-alert-triangle"></i>
                                        )}
                                    </div>
                                    <div>{message.message}</div>
                                </div>
                                <button className="btn-close" data-bs-dismiss="alert" aria-label="close"></button>
                            </div>
                        ))}
                    </>
                )}

                <form onSubmit={this.verifyOtp} autoComplete="off" noValidate>
                    <div className="mb-3">
                        <label className="form-label">
                            Verification code
                        </label>
                        <input
                            type="text"
                            name="otp"
                            className={`form-control form-control-lg text-center ${errors.otp ? 'is-invalid' : ''}`}
                            placeholder="000000"
                            value={otp}
                            onChange={this.handleOtpInput}
                            maxLength="6"
                            autoComplete="one-time-code"
                            autoFocus
                            disabled={isLoading}
                            style={{ letterSpacing: '0.5em' }}
                        />
                        {errors.otp && <div className="invalid-feedback">{errors.otp}</div>}
                    </div>

                    <div className="mb-3">
                        <button 
                            type="submit" 
                            className="btn btn-primary w-100" 
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Verifying...
                                </>
                            ) : 'Sign In'}
                        </button>
                    </div>
                    
                    <div className="text-center">
                        {otpResent ? (
                            <span className="text-success">
                                <i className="ti ti-check me-1"></i>
                                A new code has been sent to your email
                            </span>
                        ) : (
                            <span className="text-secondary">
                                Didn't receive the code?{' '}
                                <a 
                                    href="#" 
                                    onClick={(e) => { e.preventDefault(); this.resendOtp(); }}
                                >
                                    Resend
                                </a>
                            </span>
                        )}
                    </div>
                </form>
            </AuthLayout>
        );
    };

    render() {
        const { currentStep } = this.state;

        if (currentStep === 'email') {
            return this.renderEmailStep();
        }
        
        if (currentStep === 'otp') {
            return this.renderOtpStep();
        }

        return null;
    }
}

// Initialize React app
if (container) {
    const root = createRoot(container);
    root.render(<SignIn />);
}

export default SignIn;

